import { useEffect, useState } from 'react';
import { ProductsResponse, SearchService } from '@services';
import { useServiceRef } from './useServiceRef';
import { useEffectWithoutInitial } from './useEffectWithoutInitial';

export const useProducts = (
  sortBy: string,
  fetchOnFirstLoad = true,
  initialProducts: ProductsResponse | null = null
): {
  productsInfo: ProductsResponse | null;
  loadMore: () => Promise<ProductsResponse | null>;
  currentCount: number;
  isLoading: boolean;
} => {
  const searchServiceRef = useServiceRef(SearchService);
  const [ productsInfo, setProductsInfo ] = useState<ProductsResponse | null>(initialProducts);
  const [ isLoading, setIsLoading ] = useState(false);
  const effectHook = fetchOnFirstLoad ? useEffect : useEffectWithoutInitial;

  effectHook(() => {
    if (!fetchOnFirstLoad) return;
    (async (): Promise<void> => {
      setIsLoading(true);
      const productsResponse = await searchServiceRef.current.getAllProducts(0, sortBy);
      setProductsInfo(productsResponse);
      setIsLoading(false);
    })();
  }, [ sortBy ]);

  const loadMore = async (): Promise<ProductsResponse | null> => {
    const newOffset = productsInfo?.results?.length ?? 0;
    const productsResponse = await searchServiceRef.current.getAllProducts(newOffset, sortBy);

    if (productsResponse) {
      setProductsInfo(prevProductsInfo => ({
        ...productsResponse,
        results: [ ...(prevProductsInfo?.results ?? []), ...(productsResponse.results ?? []) ]
      }));
    }

    return productsResponse;
  };

  const currentCount = productsInfo?.results?.length ?? 0;

  return { productsInfo, loadMore, currentCount, isLoading };
};
